import {
  AMBIENT_SCRIBE_TEMPLATE_TYPE,
  type AmbientScribeTemplateType
} from "services/types/ambient-scribe-template.type";

export const AMBIENT_SCRIBE_TEMPLATE_TYPE_LABELS: Record<
  AmbientScribeTemplateType,
  string
> = {
  [AMBIENT_SCRIBE_TEMPLATE_TYPE.CHART_NOTE]: "Chart Note",
  [AMBIENT_SCRIBE_TEMPLATE_TYPE.AUDIOLOGY_REPORT]: "Audiology Report",
  [AMBIENT_SCRIBE_TEMPLATE_TYPE.AFTER_VISIT_SUMMARY]: "After Visit Summary"
};

export const AMBIENT_SCRIBE_TEMPLATE_TYPE_OPTIONS = [
  {
    value: AMBIENT_SCRIBE_TEMPLATE_TYPE.CHART_NOTE,
    label: AMBIENT_SCRIBE_TEMPLATE_TYPE_LABELS.ChartNote
  },
  {
    value: AMBIENT_SCRIBE_TEMPLATE_TYPE.AUDIOLOGY_REPORT,
    label: AMBIENT_SCRIBE_TEMPLATE_TYPE_LABELS.AudiologyReport
  }
];

/**
 * Type options for pickers. The After Visit Summary option only appears behind its own LD flag
 * (`AFTER_VISIT_SUMMARY_FEATURE`) — offering it with the flag off would let users create letter
 * templates no surface can use, and (worse) route the modal onto the AVS-widened appointment-type
 * projection, which 400s against a backend predating the second link slot.
 */
export function getAmbientScribeTemplateTypeOptions(
  isAfterVisitSummaryEnabled: boolean
) {
  if (!isAfterVisitSummaryEnabled) return AMBIENT_SCRIBE_TEMPLATE_TYPE_OPTIONS;
  return [
    ...AMBIENT_SCRIBE_TEMPLATE_TYPE_OPTIONS,
    {
      value: AMBIENT_SCRIBE_TEMPLATE_TYPE.AFTER_VISIT_SUMMARY,
      label: AMBIENT_SCRIBE_TEMPLATE_TYPE_LABELS.AfterVisitSummary
    }
  ];
}

/**
 * The single home for the `null ≡ ChartNote` rule (SN-19067).
 *
 * A template's `type` arrives as `null` (no backfill), `""` (stored for an explicit empty
 * write), or absent entirely (the field is only serialized when the request carried a
 * `?query=`). All three mean ChartNote server-side, so every FE read has to collapse them
 * the same way — comparing `template.type === "ChartNote"` misses the majority of rows.
 */
export function getEffectiveTemplateType(
  type: AmbientScribeTemplateType | null | undefined | string
): AmbientScribeTemplateType {
  if (type === AMBIENT_SCRIBE_TEMPLATE_TYPE.AUDIOLOGY_REPORT) {
    return AMBIENT_SCRIBE_TEMPLATE_TYPE.AUDIOLOGY_REPORT;
  }
  if (type === AMBIENT_SCRIBE_TEMPLATE_TYPE.AFTER_VISIT_SUMMARY) {
    return AMBIENT_SCRIBE_TEMPLATE_TYPE.AFTER_VISIT_SUMMARY;
  }
  return AMBIENT_SCRIBE_TEMPLATE_TYPE.CHART_NOTE;
}

export function getTemplateTypeLabel(
  type: AmbientScribeTemplateType | null | undefined | string
): string {
  return AMBIENT_SCRIBE_TEMPLATE_TYPE_LABELS[getEffectiveTemplateType(type)];
}

/** True when a template writes to the chart note (i.e. is usable by the SOAP flow). */
export function isChartNoteTemplate(
  type: AmbientScribeTemplateType | null | undefined | string
): boolean {
  return (
    getEffectiveTemplateType(type) === AMBIENT_SCRIBE_TEMPLATE_TYPE.CHART_NOTE
  );
}
